import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PopoverContainerComponent } from 'ngx-bootstrap/popover';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { CompanyService } from 'src/app/settings/services/company.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ValidationService } from 'src/app/shared/services/validation.service';

interface CompanySettings {
  [key: string]: string | boolean | undefined;
}

interface Company {
  settings?: CompanySettings;
  [key: string]: any;
}

@Component({
  selector: 'integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
  standalone: false,
})
export class IntegrationsComponent
  extends BaseEditableComponent<Company>
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('companyForm') companyForm: NgForm;

  private readonly destroy$ = new Subject<void>();

  loading = false;
  company: Company = {};
  fuseApiKeyEditable = false;
  jamfSecretEditable = false;
  mosyleAccessTokenEditable = false;
  domoClientSecretEditable = false;
  adastraClientSecretEditable = false;

  JAMF_URL =
    'https://help.risevision.com/hc/en-us/articles/24606283279252-How-do-I-enable-automatic-alerts-or-classroom-alerts-on-an-AppleTV';
  MOSYLE_URL =
    'https://help.risevision.com/hc/en-us/articles/28989490003092-Install-Rise-Player-on-Apple-TV-using-Mosyle';
  WEBSITE_REGEX = ValidationService.WEBSITE_REGEX;
  EMAIL_REGEX = ValidationService.EMAIL_REGEX;

  actions = {
    saveAction: {
      id: 'saveButton',
      label: 'Save',
      action: () => this.save(),
      requireRole: 'ua sa',
      disabled: false,
    },
    cancelAction: {
      id: 'cancelButton',
      label: 'Cancel',
      action: () => this.cancel(),
    },
  };

  constructor(
    private location: Location,
    public companyService: CompanyService,
    private companyStateService: CompanyStateService,
  ) {
    super();
  }

  redirect(url: string) {
    window.open(url, '_blank');
  }

  onPopoverShown(event: PopoverContainerComponent) {
    setTimeout(() => {
      const element = [...document.querySelectorAll('popover-container')].pop();
      if (element) {
        element.id = event.popoverId;
        (element as HTMLElement).focus();
      }
    });
  }

  ngOnInit() {
    this.oldObject = this.cloneDeep(this.company);
    this.loadCompanyData();
  }

  ngAfterViewInit() {
    if (this.companyForm) {
      this.actions.saveAction.disabled = this.companyForm.invalid;
      this.companyForm.statusChanges
        ?.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.actions.saveAction.disabled = this.companyForm.invalid;
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadCompanyData() {
    const companyId = this.companyStateService.getSelectedCompanyId();
    if (!companyId) {
      return;
    }

    this.loading = true;
    try {
      await this.companyService.getCompany(companyId);
      this.company = this.companyService.company;
      if (!this.company.settings) {
        this.company.settings = {};
      }
      if (typeof this.company.settings.fuseEnabled === 'string') {
        this.company.settings.fuseEnabled =
          this.company.settings.fuseEnabled === 'true';
      }
      if (typeof this.company.settings.jamfEnabled === 'string') {
        this.company.settings.jamfEnabled =
          this.company.settings.jamfEnabled === 'true';
      }
      if (typeof this.company.settings.mosyleEnabled === 'string') {
        this.company.settings.mosyleEnabled =
          this.company.settings.mosyleEnabled === 'true';
      }
      if (typeof this.company.settings.domoEnabled === 'string') {
        this.company.settings.domoEnabled =
          this.company.settings.domoEnabled === 'true';
      }
      if (typeof this.company.settings.adastraEnabled === 'string') {
        this.company.settings.adastraEnabled =
          this.company.settings.adastraEnabled === 'true';
      }
      this.updateEditableFields();
      this.oldObject = this.cloneDeep(this.company);
    } catch (error) {
      console.error('Failed to load company data:', error);
      // Consider showing user-friendly error message
    } finally {
      this.loading = false;
    }
  }

  updateEditableFields() {
    if (this.company?.settings) {
      if (this.company.settings.fuseEnabled) {
        this.fuseApiKeyEditable = !this.company.settings.fuseApiKey;
      }
      if (
        this.company.settings.jamfEnabled &&
        !this.company.settings.mosyleEnabled
      ) {
        this.jamfSecretEditable = !this.company.settings.jamfClientSecret;
      }
      if (
        this.company.settings.mosyleEnabled &&
        !this.company.settings.jamfEnabled
      ) {
        this.mosyleAccessTokenEditable =
          !this.company.settings.mosyleAccessToken;
      }
      if (this.company.settings.domoEnabled) {
        this.domoClientSecretEditable = !this.company.settings.domoClientSecret;
      }
      if (this.company.settings.adastraEnabled) {
        this.adastraClientSecretEditable =
          !this.company.settings.adastraClientSecret;
      }
    }
  }

  editFuseApiKey() {
    this.fuseApiKeyEditable = true;
  }

  editJamfSecret() {
    this.jamfSecretEditable = true;
  }

  editMosyleAccessToken() {
    this.mosyleAccessTokenEditable = true;
  }

  editDomoClientSecret() {
    this.domoClientSecretEditable = true;
  }

  onDomoClientSecretBlur() {
    if (this.company?.settings?.domoClientSecret) {
      this.domoClientSecretEditable = false;
    }
  }

  editAdastraClientSecret() {
    this.adastraClientSecretEditable = true;
  }

  onAdastraClientSecretBlur() {
    if (this.company?.settings?.adastraClientSecret) {
      this.adastraClientSecretEditable = false;
    }
  }

  async save() {
    if (!this.companyForm?.valid) {
      return;
    }
    try {
      await this.companyService.save(this.companyForm, this.oldObject);
      this.companyService.clearCompanyCredentials();
      this.oldObject = this.cloneDeep(this.company);
    } catch (error) {
      // Error is already handled by companyService
    }
  }

  cancel() {
    this.bypassUnsavedChanges();
    this.location.back();
  }

  canDeactivate(): boolean {
    return !this.oldObject || this.isDeepEqual(this.oldObject, this.company);
  }
}
